import {DonationComponent} from "../donation/donation.component";
import {ChangeDetectorRef, Component, OnInit, Renderer2} from "@angular/core";
import {OptionService} from "../option/option.service";
import {DonationService} from "../donation/donation.service";
import {UrlService} from "../url/url.service";
import {ShopCartService} from "./shop.cart.service";
import {Product} from "./shop.product.model";
import {TranslateService} from "@ngx-translate/core";
import {HttpClient} from "@angular/common/http";
import {DecimalPipe} from "@angular/common";
import {ElementService} from "../service/element.service";
import {StripeService} from "../payment/payment.stripe.service";
import { Donation } from "donation/donation";
import {ContactComponent} from "../contact/contact.component";
import { BundlesService } from "service/bundles.service";
import { Bundle } from "./shop.bundle.model";

declare let __twingle_products: any;
declare let __twingle_categories: any;

/**
 * Component for the donation shop that extend the existing DonationComponent and adds some steps.
 */
@Component({
    selector: 'shop',
    templateUrl: './shop.component.html',
    providers: [OptionService, DonationService, UrlService, DecimalPipe]
})
export class ShopComponent extends DonationComponent implements OnInit {

    /**
     * The donation object for the shop (default)
     */
    shopDonation: Donation;

    /**
     * The donation object for the digital download process
     */
    digitalDownloadDonation: Donation;

    /**
     * Flag if the digital download process is active
     */
    isDigitalDownloadProcess = false;

    /**
     * Show a single product.
     */
    currentItem: Product | Bundle;

    /**
     * The available screensizes we want to check for
     */
    screenSizes: any = {
        small: 0,
        medium: 540,
        large: 768,
        xlarge: 1200
    }

    /**
     * constructor
     * @param optionService
     * @param translate
     * @param donationService
     * @param http
     * @param changeDetector
     * @param urlService
     * @param renderer
     * @param decimalPipe
     * @param cartService
     * @param elementService
     * @param stripeService
     */
    constructor(protected optionService: OptionService,
                protected translate: TranslateService,
                protected donationService: DonationService,
                protected http: HttpClient,
                protected changeDetector: ChangeDetectorRef,
                protected urlService: UrlService,
                protected renderer: Renderer2,
                protected decimalPipe: DecimalPipe,
                protected cartService: ShopCartService,
                protected elementService: ElementService,
                protected stripeService: StripeService,
                protected bundleService: BundlesService
    ) {
        super(optionService, translate, donationService, http, changeDetector, urlService, renderer, decimalPipe, elementService, stripeService);

        // set the donation object for the shop
        this.shopDonation = this.donation;
        // set donation to the cart service to save ordered products
        this.cartService.setDonation(this.shopDonation);

        // set the available categories to the cart service.
        let categories = [];
        if(typeof  __twingle_categories !== "undefined" && __twingle_categories.length > 0) {
            categories = __twingle_categories;
        }
        this.cartService.setCategories(categories);
    }

    /**
     * @inheritDoc
     */
    ngOnInit(): any {
        super.ngOnInit();

        // we have no "start" step, show the list
        if(this.step === "start") {
            this.step = "list";
        }

        // check if a special product / or bundle should be visible on start
        const productId: string = this.urlService.findGetParameter(this.urlService.PARAMETER_PRODUCT);
        const bundleId: string = this.urlService.findGetParameter(this.urlService.PARAMETER_BUNDLE);
        
        if((productId || bundleId) && typeof __twingle_products !== "undefined" && __twingle_products.length > 0) {
            // try to find the product and show if if it is available
            if (productId) {
                const data = __twingle_products.find(element => element.hasOwnProperty('id') && element.id === parseInt(productId) && element.type === 'product');
                if (data) {
                    const product = new Product();
                    Object.assign(product, data);
                    product.checkIfPurchasable();
                    this.showItem(product);
                }
            }
            if (bundleId) {
                const data = __twingle_products.find(element => element.hasOwnProperty('id') && element.id === parseInt(bundleId) && element.type === 'bundle');
                if (data) {
                    const bundle = new Bundle();
                    Object.assign(bundle, data);
                    this.bundleService.prepareBundleInformation(bundle);
                    this.showItem(bundle);
                }
            }              
        }

        // set donation target
        const donationTargets = this.optionService.getDonationTargets();
        if (donationTargets.length > 0 && !this.donation.target) {
            // case 1: no target in donation presettet, use the first in the list
            this.donation.target = donationTargets[0].donation_target;
        }
        else if (!this.donation.target) {
            // case 2: no target presetet and no available, use a generic one
            this.translate.get('target.default').subscribe((res: string) => {
                this.donation.target = res;
            });
        }

        // for DEV Purpose only: show the cart
        // this.cartService.addItem(__twingle_products[0], 2);
        // this.cartService.addItem(__twingle_products[1], 1);
        // this.setStep('cart');
    }

    /**
     * Can be called if a single product should be shown.
     * @param product
     */
    showItem(item: Product | Bundle): void {
        this.currentItem = item;
        this.setStep('detail', true);
    }

    /**
     * Show the download page for a certain product.
     */
    showDownloadForProduct(product: Product): void {
        this.currentItem = product;
        this.setStep('download', true);
    }

    /**
     * prepare the digital donation
     */
    prepareDigitalDonation(): void {
        // create a copy of the donation with no products and no amount
        this.digitalDownloadDonation = new Donation();
        this.digitalDownloadDonation.copyData(this.donation);
        this.digitalDownloadDonation.products = [];
        this.digitalDownloadDonation.amount = 0;
        this.digitalDownloadDonation.extraAmount = 0;
    }

    /**
     * Sets the current step in the donation process.
     * If the new step is "download", prepares for digital donation process.
     * If the new step is "start", "list", or "detail", ends digital donation process.
     * @param newStep
     * @param scroll if true, the page tries to scroll upwards
     * @override Overrides the setStep method in the superclass.
     */
    setStep(newStep: string, scroll: boolean = false): void {
        // check if we started the digital download process
        if (newStep === "download") {
            this.prepareDigitalDonation();
            this.isDigitalDownloadProcess = true;
            this.donation = this.digitalDownloadDonation;
            // set a new token for the digital download donation after we switched the donation object
            this.setToken();
        }
        // check if we are finished with the digital download process
        if (newStep === "start" || newStep === "list" || newStep === "detail") {
            this.isDigitalDownloadProcess = false;
            this.donation = this.shopDonation;
        }
        // if we enter the download process or the cart
        if (newStep === 'download' || newStep === 'cart') {
            // reset extra amount in the form
            if (this.widgetPaymentForm.controls['extra_amount']) {
                this.widgetPaymentForm.controls['extra_amount'].setValue(false);
            }
        }

        // redirect to another url instead of showing the thankyou page
        if (newStep === 'thankyou' && this.optionService.getOption('thankyou_redirect_url')?.length > 0) {
          const url = new URL(this.optionService.getOption('thankyou_redirect_url'));
          const params = [];
          // add presets
          for (const field of ContactComponent.allContactFields) {
            if (this.donation.contactData.data[field]) {
              params.push(`tw_${field}=${encodeURIComponent(this.donation.contactData.data[field])}`)
            }
          }
          // add email
          if (this.donation.userEmail) {
            params.push(`tw_email=${this.donation.userEmail}`);
          }
          // add campaign if available
          if (this.donation.metaData.campaign) {
            params.push(`${this.urlService.PARAMETER_CAMPAIGN}=${this.donation.metaData.campaign}`);
          }

          // build querystring
          let search = url.search;
          if (search.includes('?')) {
            search = search + '&' + params.join('&');
          }
          else {
            search = params.join('&');
          }
          url.search = search;

          // redirect to this page
          window.location.href = url.toString();
          return;
        }

        // call super
        super.setStep(newStep, scroll);
    }
}
